// src/components/ProcessManager/ProcessManagerTrackingOverview.tsx
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  AccountBalance,
  Business,
  Diversity3,
  HomeWork,
  TrackChanges,
} from '@mui/icons-material';
import React, { useState } from 'react';
import {
  useCourtAdministrativeUnitAssignments,
  useCourts,
} from '../Aemter/hooks';
import { useGetProcessManagerTracking } from '../hooks/reactQuerys/ProcessManagerQueries';
import { ProcessManagerTrackingBulkTable } from './Childs/ProcessManagerTrackingBulkTable';
import { ProcessManagerCommercialTrackingTable } from './Childs/ProcessManagerCommercialTrackingTable';
import { ProcessManagerHbciTrackingTable } from './Childs/ProcessManagerHbciTrackingTable';
import { useGetRealEstateLandRegisterEntries } from '../hooks/reactQuerys/RealEstateQueries';
import { useGetAppArray } from '../hooks/reactQuerys/AppQueries';
import { LandRegisterTrackingRunDialog } from './LandRegister/LandRegisterTrackingRunDialog';

/**
 * Globale Übersicht aller laufenden Informationsverfolgungen über alle
 * Vorgänge hinweg. Wenn noch nichts angelegt ist, zeigen wir analog zum
 * Dialogeingang Onboarding-Karten mit kurzer Erklärung pro Tracking-Typ —
 * inkl. Hinweis, wo der Notar diese im Vorgang anlegen kann.
 */
export const ProcessManagerTrackingOverview: React.FC = () => {
  const {
    courtAdministrativeUnitAssignments,
    isLoadingCourtAdministrativeUnitAssignments,
  } = useCourtAdministrativeUnitAssignments();
  const { courts, isLoadingCourts } = useCourts();
  const {
    processManagerTrackings,
    isLoadingManagerTrackings,
    refetchManagerTrackings,
  } = useGetProcessManagerTracking();
  const {
    realEstateLandRegisterEntries,
    isLoadingRealEstateLandRegisterEntries,
  } = useGetRealEstateLandRegisterEntries();
  // HBCI-Modul-Gate — Onboarding-Karte „Notaranderkonto" und die
  // Notaranderkonten-Section werden nur angezeigt, wenn die Erweiterung
  // ``hbci_connection`` lizenziert/aktiv ist.
  const { appArray } = useGetAppArray();
  const hbciConnectionActive =
    appArray?.find(a => a.system_key === 'hbci_connection')?.IsEffective ===
    true;

  const [showClosed, setShowClosed] = useState(false);

  if (
    isLoadingRealEstateLandRegisterEntries ||
    isLoadingCourtAdministrativeUnitAssignments ||
    isLoadingCourts ||
    isLoadingManagerTrackings
  ) {
    return <CircularProgress />;
  }

  if (
    !realEstateLandRegisterEntries ||
    !courtAdministrativeUnitAssignments ||
    !courts ||
    !processManagerTrackings
  ) {
    return <Alert severity='error'>Es ist ein Fehler aufgetreten!</Alert>;
  }

  const filtered = processManagerTrackings.filter(
    x => showClosed || x.idProcessManagerState === 1
  );

  const grundbuchPM = filtered.filter(x =>
    [10, 11].includes(x.idProcessManagerType)
  );
  const handelsregisterPM = filtered.filter(x => x.idProcessManagerType === 20);
  const gesellschafterlistePM = filtered.filter(
    x => x.idProcessManagerType === 21
  );
  const notaranderkontoPM = filtered.filter(x => x.idProcessManagerType === 22);

  const isEmpty =
    grundbuchPM.length === 0 &&
    handelsregisterPM.length === 0 &&
    gesellschafterlistePM.length === 0 &&
    notaranderkontoPM.length === 0;

  /* -------------------------------------------------------------------- */
  /* Onboarding-Karten (Empty-State)                                      */
  /* -------------------------------------------------------------------- */
  const onboardingCards: Array<{
    key: string;
    icon: React.ReactElement;
    title: string;
    chip: string;
    description: string;
  }> = [
    {
      key: 'grundbuch',
      icon: <HomeWork color='primary' />,
      title: 'Grundbuch-Aktualitätsnachweis',
      chip: 'Type 10/11',
      description:
        'Prüft regelmäßig, ob im Grundbuch des hinterlegten Vertragsgegenstands eine Änderung eingetragen wurde — z. B. neuer Eigentümer, Änderungen in Abt. II/III oder Markentabellen-Updates.',
    },
    {
      key: 'handelsregister',
      icon: <Business color='primary' />,
      title: 'Handelsregister-Beobachtung',
      chip: 'Type 20',
      description:
        'Überwacht das Handelsregister einer im Vorgang hinterlegten Firma. Bei Geschäftsführerwechsel, Sitzverlegung oder neuen Eintragungen erhalten Sie automatisch eine Benachrichtigung.',
    },
    {
      key: 'gesellschafterliste',
      icon: <Diversity3 color='primary' />,
      title: 'Gesellschafterliste',
      chip: 'Type 21',
      description:
        'Verfolgt aktualisierte Gesellschafterlisten der hinterlegten Gesellschaft. Sobald eine neue Liste hinterlegt wird, wird der Vorgang informiert.',
    },
    // Notaranderkonto nur, wenn die hbci_connection-Erweiterung aktiv ist —
    // sonst koennte der Notar es im Vorgang ohnehin nicht anlegen.
    ...(hbciConnectionActive
      ? [
          {
            key: 'notaranderkonto',
            icon: <AccountBalance color='primary' />,
            title: 'Notaranderkonto-Saldo',
            chip: 'Type 22',
            description:
              'Ruft regelmäßig den Saldo eines Notaranderkontos per HBCI/FinTS ab. Optional kann eine Zielsumme hinterlegt werden — sobald diese erreicht ist, werden automatisch die definierten Automationen ausgelöst.',
          },
        ]
      : []),
  ];

  return (
    <>
      <Typography variant='h4'>
        Nachverfolgungen
        <FormControlLabel
          sx={{ float: 'right' }}
          control={
            <Switch
              checked={showClosed}
              onChange={() => setShowClosed(!showClosed)}
              name='toggleClosed'
            />
          }
          label='Abgeschlossene Nachverfolgungen'
          labelPlacement='start'
        />
      </Typography>

      <Box sx={{ mb: 2 }}>
        <LandRegisterTrackingRunDialog onCompleted={refetchManagerTrackings} />
        <Typography variant='caption' color='text.secondary' sx={{ ml: 1 }}>
          Grundbuch-Nachverfolgungen werden nicht mehr automatisch ausgeführt –
          starten Sie sie hier mit Ihrem persönlichen Zugang.
        </Typography>
      </Box>

      {isEmpty ? (
        <Box sx={{ mt: 3 }}>
          <Card variant='outlined' sx={{ mb: 3, bgcolor: 'action.hover' }}>
            <CardContent>
              <Stack direction='row' spacing={1.5} alignItems='center'>
                <TrackChanges color='primary' fontSize='large' />
                <Box>
                  <Typography variant='h6'>
                    Noch keine Nachverfolgungen eingerichtet
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    DiNo kann im Hintergrund prüfen, ob sich für Ihre laufenden
                    Vorgänge relevante Änderungen ergeben — und Sie automatisch
                    benachrichtigen.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Typography variant='subtitle1' sx={{ mb: 1 }}>
            Was Sie überwachen können
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Nachverfolgungen werden im Kontext eines Vorgangs angelegt: öffnen
            Sie den jeweiligen Vorgang, wechseln Sie in den Tab{' '}
            <strong>Vorgangsmanager</strong> und klicken Sie unter{' '}
            <strong>III. Informationsverfolgung</strong> auf das{' '}
            <strong>+</strong>-Symbol.
          </Typography>

          <Grid container spacing={2}>
            {onboardingCards.map(c => (
              <Grid item xs={12} sm={6} md={6} key={c.key}>
                <Card variant='outlined' sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack
                      direction='row'
                      spacing={1.5}
                      alignItems='flex-start'
                    >
                      <Box sx={{ pt: 0.5 }}>{c.icon}</Box>
                      <Box sx={{ flex: 1 }}>
                        <Stack
                          direction='row'
                          spacing={1}
                          alignItems='center'
                          sx={{ mb: 0.5 }}
                        >
                          <Typography variant='subtitle1'>{c.title}</Typography>
                          <Chip
                            size='small'
                            variant='outlined'
                            label={c.chip}
                          />
                        </Stack>
                        <Typography variant='body2' color='text.secondary'>
                          {c.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Alert severity='info' sx={{ mt: 3 }}>
            Sobald die erste Nachverfolgung angelegt und der Vorgang gespeichert
            ist, erscheint sie an dieser Stelle — gruppiert nach Typ.
            Automationen (z. B. Mail an Mandant, Anforderungs-Update) können pro
            Nachverfolgung direkt am Eintrag hinterlegt werden.
          </Alert>
        </Box>
      ) : (
        <>
          {/* Grundbuch — flache Tabelle mit Mehrfachauswahl + Bulk-Aktionen;
              zeigt auch blatt-zentrische Trackings (data.idGrundbuchblatt). */}
          {grundbuchPM.length > 0 && (
            <ProcessManagerTrackingBulkTable
              key='grundbuch-tracking'
              title='Grundbuch'
              processManagerArray={grundbuchPM}
              realEstateLandParcelEntries={realEstateLandRegisterEntries}
              adminUnitsAssign={courtAdministrativeUnitAssignments}
              onChanged={refetchManagerTrackings}
            />
          )}

          {/* Handelsregister */}
          {handelsregisterPM.length > 0 && (
            <ProcessManagerCommercialTrackingTable
              key='handelsregister-tracking'
              title='Handelsregister'
              courts={courts}
              processManagerArray={handelsregisterPM}
            />
          )}

          {/* Gesellschafterliste */}
          {gesellschafterlistePM.length > 0 && (
            <ProcessManagerCommercialTrackingTable
              key='gesellschafterliste-tracking'
              title='Gesellschafterliste'
              courts={courts}
              processManagerArray={gesellschafterlistePM}
            />
          )}

          {/* Notaranderkonten — nur wenn Modul aktiv ODER schon Daten da */}
          {(hbciConnectionActive || notaranderkontoPM.length > 0) && (
            <ProcessManagerHbciTrackingTable
              key='hbci-tracking'
              processManagerArray={notaranderkontoPM}
            />
          )}
        </>
      )}
    </>
  );
};
